// Approval progress for claims and variations (V0.4.3): one step per item, shared by the lists, the summary and
// the item page. Steps: SWI review → SWI approved ("Ready to proceed") → Client approved → Paid (claims) /
// Complete (variations, only when a PM ticks it). Hold and Rejected sit to the side.
// Imported Smartsheet items count too: "Ready to Proceed" = Yes → SWI approved; "Progress of works" → work progress.

export type Step = 'review' | 'swi' | 'client' | 'paid' | 'complete' | 'hold' | 'rejected';
export type WorkKey = 'none' | 'quarter' | 'half' | 'three_quarter' | 'full';

export type ProgressRow = {
  type: string;
  status: string;
  stage: string | null;
  amount: number | null;
  submitted_on: string | null;
  created_at: string;
  ss_created_at: string | null;
  data: Record<string, unknown> | null;
  swi_approved_at: string | null;
  client_approved_at: string | null;
  work_progress: string | null;
  paid_total: number | null;
  paid_on: string | null;
};

export const STEP_LABEL: Record<'claim' | 'variation', Record<Step, string>> = {
  claim: { review: 'SWI review', swi: 'SWI approved · with finance', client: 'Client approved · to pay', paid: 'Paid', complete: 'Paid', hold: 'On hold', rejected: 'Rejected' },
  variation: { review: 'SWI review', swi: 'SWI approved · with client', client: 'Client approved', paid: 'Complete', complete: 'Complete', hold: 'On hold', rejected: 'Rejected' },
};
/** 4-part strip: how far along each step is (colour per part). */
export const STRIP: Record<Step, [string, string, string, string]> = {
  review: ['#A3470D', '#EAD6C2', '#EAD6C2', '#EAD6C2'],
  hold: ['#F2C29C', '#EAD6C2', '#EAD6C2', '#EAD6C2'],
  swi: ['#8CC487', '#8CC487', '#EAD6C2', '#EAD6C2'],
  client: ['#2E6B3A', '#2E6B3A', '#2E6B3A', '#EAD6C2'],
  paid: ['#C9D3CA', '#C9D3CA', '#C9D3CA', '#C9D3CA'],
  complete: ['#C9D3CA', '#C9D3CA', '#C9D3CA', '#C9D3CA'],
  rejected: ['#E4DACD', '#E4DACD', '#E4DACD', '#E4DACD'],
};

export const WORK: { key: WorkKey; label: string; q: number }[] = [
  { key: 'none', label: 'Not started', q: 0 },
  { key: 'quarter', label: '¼ done', q: 1 },
  { key: 'half', label: '½ done', q: 2 },
  { key: 'three_quarter', label: '¾ done', q: 3 },
  { key: 'full', label: 'Fully done', q: 4 },
];
const SS_WORK: Record<string, WorkKey> = { Empty: 'none', Quarter: 'quarter', Half: 'half', 'Three Quarter': 'three_quarter', Full: 'full' };

const DAY = 86_400_000;
const daysFrom = (v: string | null | undefined) => {
  if (!v) return null;
  const t = new Date(v.length <= 10 ? v + 'T00:00:00' : v).getTime();
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((Date.now() - t) / DAY));
};
export const raisedOn = (r: Pick<ProgressRow, 'submitted_on' | 'ss_created_at' | 'created_at'>) => r.submitted_on ?? (r.ss_created_at ?? r.created_at ?? '').slice(0, 10);

export type Progress = {
  step: Step;
  label: string;
  /** claims: architect has certified ('cert') or it is marked ready for certification ('for') */
  cert: 'cert' | 'for' | null;
  /** SWI/client approved over 90 days ago with no payment recorded (claims) / no client approval (variations) */
  stale: boolean;
  /** days since it reached its current step (or was raised) */
  days: number;
  work: WorkKey | null;
  /** variations: % of the amount paid so far (0–100), null when nothing paid */
  paidPct: number | null;
};

export function swiApproved(r: ProgressRow): boolean {
  return Boolean(r.swi_approved_at) || String(r.data?.['Ready to Proceed'] ?? '') === 'Yes';
}

export function progressOf(r: ProgressRow): Progress {
  const claim = r.type === 'claim';
  const rtp = String(r.data?.['Ready to Proceed'] ?? '');
  let step: Step;
  if (r.status === 'rejected' || r.status === 'void') step = 'rejected';
  else if (claim && (r.status === 'paid' || r.stage === 'paid')) step = 'paid';
  else if (!claim && r.status === 'complete') step = 'complete';
  else if (r.status === 'hold' || rtp === 'Hold' || rtp === 'in revision') step = 'hold';
  else if (r.client_approved_at || (claim ? r.stage === 'client_approval' || (r.status === 'approved' && !r.stage) : r.status === 'approved')) step = 'client';
  else if (swiApproved(r) || (claim && (r.stage === 'architect_review' || r.stage === 'certified'))) step = 'swi';
  else step = 'review';

  const cert: Progress['cert'] = !claim
    ? null
    : r.stage === 'certified' || r.stage === 'client_approval' || r.data?.['Docu-Sign'] === true
      ? 'cert'
      : r.stage === 'architect_review' || r.data?.['Ready for Certification'] === true
        ? 'for'
        : null;
  const since = step === 'swi' ? (r.swi_approved_at ?? raisedOn(r)) : step === 'client' ? (r.client_approved_at ?? raisedOn(r)) : step === 'paid' ? (r.paid_on ?? raisedOn(r)) : raisedOn(r);
  const days = daysFrom(since) ?? 0;
  const stale = claim ? (step === 'swi' || step === 'client') && days > 90 : step === 'swi' && days > 90;
  const ss = SS_WORK[String(r.data?.['Progress of works'] ?? '')] ?? null;
  const work = claim ? null : ((r.work_progress as WorkKey | null) ?? ss);
  const amt = Number(r.amount ?? 0);
  const paidPct = !claim && r.paid_total && amt > 0 ? Math.min(100, Math.round((Number(r.paid_total) / amt) * 100)) : null;
  return { step, label: STEP_LABEL[claim ? 'claim' : 'variation'][step], cert, stale, days, work, paidPct };
}

export const workQ = (w: WorkKey | null) => (w ? (WORK.find((x) => x.key === w)?.q ?? 0) : null);
